import React from 'react';
import { crnaNameplateColor, doctorBullpenColor, studentColorFor, NAMEPLATE_COLORS } from './staffNameplates.ts';
import { earliestShiftByEmployee, laterShiftByEmployee, providerCardShift } from './boardViewHelpers.ts';
import { utcToLocalTime } from '../utils/timezone.ts';
import { FitText } from './RoomNamePlates.tsx';
import EmployeeDetailsPopover from './EmployeeDetailsPopover.tsx';
import type { DoctorSupervision } from './EmployeeDetailsPopover.tsx';
import type { Employee } from '../services/api.ts';
import { colorForHospital, darkenHex } from './hospitalColors.ts';

// The provider-card prop bundle BoardView builds for a bullpen person — the SAME card the board nameplates
// render. Every field mirrors an EmployeeDetailsPopover prop and every one is optional: the room-relative
// ones are absent for someone not yet placed, and `employee` is absent when the builder cannot resolve the id
// (the brick then falls back to its own record rather than opening an empty card).
type BullpenCardProps = Partial<Omit<React.ComponentProps<typeof EmployeeDetailsPopover>, 'children' | 'triggerStyle'>>;

// The bullpen: available providers for the day as draggable "bricks", in three columns
// (Anesthesiologists | CRNAs | Students). Non-pecking-order staff are grouped and labeled by
// their shift WINDOW (e.g. "0630-1500"); pecking-order doctors get a numbered group; students
// group by competency. Static — it does NOT change with the timeline bar. Staffing a room removes
// a provider from the bullpen (excludeIds); freeing a room returns them. Click a brick for the card.
//
// EXCEPT someone COMING BACK for a later shift today. Braden 2026-09-20: "If someone is coming back for a later
// shift, don't go ahead and assign them but don't leave them invisible... show them as available in the bullpen for
// the 1500-2300 shift." Staffed in the morning, they used to vanish for the WHOLE day, so the runner could neither
// see nor use the block they return for. They now stay, grouped under that LATER window with a ↩ badge, and stay
// draggable — the allocator still never plans them into it (Symphony's returnsLaterMin); the runner does.

interface EmployeeLike { employeeId: string; firstName: string; lastName: string; employeeType: string; studentCompetency?: string; homeHospitalId?: string; }
interface ShiftLike { employeeId: string; startTimeUtc?: string; endTimeUtc?: string; order?: number | null }
interface HospitalLike { hospitalId: string; name: string; color?: string }
// One room that still needs a provider (doctor or CRNA), for the bullpen NEED nameplate.
// One room that still needs a provider (doctor or CRNA), for the bullpen NEED nameplate. `time` is the
// single case that needs staffing -- the room's governing case -- not every case in the room.
export interface StaffNeed { roomLabel: string; hospital: string; hospitalId: string; time: string }

/** Needs grouped by site, keeping the caller's ordering (BoardView sorts them by first case time). */
const needsBySite = (needs: StaffNeed[]): [string, StaffNeed[]][] => {
  const m = new Map<string, StaffNeed[]>();
  for (const n of needs) { const a = m.get(n.hospital) || []; a.push(n); m.set(n.hospital, a); }
  return [...m.entries()];
};

const localHM = (utc?: string): string => (utc ? utcToLocalTime(utc) : '');            // "05:30"
const hhmm = (utc?: string): string => localHM(utc).replace(':', '');                   // "0530"
const localMin = (utc?: string): number => { const t = localHM(utc); return t ? (+t.slice(0, 2)) * 60 + (+t.slice(3, 5)) : 99999; };

const Bullpen: React.FC<{ employees: EmployeeLike[]; shifts: ShiftLike[]; hospitals?: HospitalLike[]; excludeIds?: Set<string>; dimIds?: Set<string>; supervisionFor?: (id: string) => DoctorSupervision; onRemoveStaff?: (fromScheduleId: string, employeeId: string) => void; colorOverrideFor?: (employeeId: string) => string | undefined; orderOverride?: Record<string, number>; doctorNeeds?: StaffNeed[]; doctorNeedCount?: number; crnaNeeds?: StaffNeed[]; cardFor?: (employeeId: string) => BullpenCardProps }> = ({ employees, shifts, hospitals = [], excludeIds, dimIds, supervisionFor, onRemoveStaff, colorOverrideFor, orderOverride, doctorNeeds = [], doctorNeedCount, crnaNeeds = [], cardFor }) => {
  const [dropActive, setDropActive] = React.useState(false);
  // Which NEED nameplate's room list is expanded (click a NEED brick to see the rooms/times it covers).
  const [openNeed, setOpenNeed] = React.useState<null | 'doctor' | 'crna'>(null);
  // Where to pin the needs panel. The badge lives inside the column's scroll box
  // (maxHeight/overflow), which CLIPS an absolutely-positioned child on both axes — so the panel is
  // position:fixed, anchored off the badge's own rect. Braden 2026-10-03: "don't make the rooms
  // scrollable. Pop open another box with a wider display that shows where all of the needs are."
  const [needAnchor, setNeedAnchor] = React.useState<{ top: number; right: number } | null>(null);
  React.useEffect(() => {
    if (!openNeed) return;
    const close = () => setOpenNeed(null);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [openNeed]);
  const hospitalNameById = new Map(hospitals.map(h => [h.hospitalId, h.name]));
  // Full hospital records, for the NEED panel's site-coloured cards (colorForHospital needs `color`).
  const hospObjById = new Map(hospitals.map(h => [h.hospitalId, h]));

  // Earliest shift per employee (drives grouping, ordering, and shift color) — the same shared pick the
  // provider card resolves, so a brick and the card it opens always describe the same shift.
  const shiftByEmp = earliestShiftByEmployee(shifts);
  // The block a staffed provider comes BACK for, if any — the only reason someone in excludeIds still shows here.
  const laterShiftByEmp = laterShiftByEmployee(shifts);
  const returningShift = (id: string) => (excludeIds?.has(id) ? laterShiftByEmp.get(id) : undefined);
  const availableIds = new Set(shifts.map(s => s.employeeId));
  const available = employees.filter(e => availableIds.has(e.employeeId)
    && (!(excludeIds?.has(e.employeeId)) || !!returningShift(e.employeeId)));
  // A returning provider is described by the shift they are coming back FOR, so they group and colour under that
  // window (the "1500-2300" group) instead of the morning one they are already working.
  const displayShift = (id: string) => returningShift(id) ?? shiftByEmp.get(id);

  // Pecking-order number: a manual reorder from the Anesthesiologists tab (orderOverride) wins over the
  // shift's stored order, so dragging docs there re-numbers them here too.
  const orderOf = (id: string): number | null => {
    if (orderOverride && orderOverride[id] !== undefined) return orderOverride[id];
    const o = displayShift(id)?.order;
    return o === undefined || o === null ? null : o;
  };
  const windowLabel = (id: string): string => {
    const s = displayShift(id);
    return s && s.startTimeUtc && s.endTimeUtc ? `${hhmm(s.startTimeUtc)}-${hhmm(s.endTimeUtc)}` : 'No shift';
  };
  const byLastName = (a: EmployeeLike, b: EmployeeLike) => a.lastName.localeCompare(b.lastName);

  // Group a list of workers by their shift-window label, ordered by start then end time.
  const groupByWindow = (list: EmployeeLike[]) => {
    const groups = new Map<string, { label: string; startMin: number; endMin: number; items: EmployeeLike[] }>();
    for (const e of list) {
      const lbl = windowLabel(e.employeeId);
      const s = displayShift(e.employeeId);
      if (!groups.has(lbl)) groups.set(lbl, { label: lbl, startMin: localMin(s?.startTimeUtc), endMin: localMin(s?.endTimeUtc), items: [] });
      groups.get(lbl)!.items.push(e);
    }
    // Sort groups by END time first (so a 1900 shift sorts after 1700 regardless of a 0530/0630 start),
    // then by start time as a tiebreaker.
    return [...groups.values()].sort((a, b) => a.endMin - b.endMin || a.startMin - b.startMin);
  };

  const doctors = available.filter(e => e.employeeType === 'doctor');
  const crnas = available.filter(e => e.employeeType === 'nurse');
  const students = available.filter(e => e.employeeType.startsWith('nurse-student'));

  const shiftDocGroups = groupByWindow(doctors.filter(e => orderOf(e.employeeId) === null));
  const orderedDocs = doctors.filter(e => orderOf(e.employeeId) !== null).sort((a, b) => orderOf(a.employeeId)! - orderOf(b.employeeId)!);
  // CRNAs mirror the doctors: pecking-order CRNAs (order set) get a numbered "Pecking order" group; the rest
  // are grouped by shift window. So a pecking-order CRNA shows its #N on its bullpen brick, like the doctor tab.
  const shiftCrnaGroups = groupByWindow(crnas.filter(e => orderOf(e.employeeId) === null));
  const orderedCrnas = crnas.filter(e => orderOf(e.employeeId) !== null).sort((a, b) => orderOf(a.employeeId)! - orderOf(b.employeeId)!);

  const brick = (e: EmployeeLike, color: string, number?: number) => {
    const shift = displayShift(e.employeeId);
    // B1: a person marked "Off" (sent home) STAYS on the bullpen, greyed out, rather than vanishing — so the card
    // that opened doesn't unmount and the runner can still see (and un-off) them. Dimmed bricks aren't draggable
    // onto a room (they're home), but stay clickable for the card.
    const dimmed = dimIds?.has(e.employeeId) ?? false;
    const backAt = returningShift(e.employeeId);   // staffed now, but back for a later block today
    const inner = (
      <div
        draggable={!dimmed}
        onDragStart={dimmed ? undefined : (ev) => {
          ev.dataTransfer.setData('application/x-staff', JSON.stringify({ employeeId: e.employeeId, employeeType: e.employeeType }));
          ev.dataTransfer.effectAllowed = 'copy';
        }}
        title={dimmed ? `${e.firstName} ${e.lastName} — sent home`
          : backAt ? `${e.firstName} ${e.lastName} — already staffed today; back at ${localHM(backAt.startTimeUtc)}`
          : undefined}
        style={{
          background: color, color: '#fff', fontWeight: 600, borderRadius: '5px',
          padding: '0 6px', height: '22px', display: 'flex', alignItems: 'center', gap: '5px',
          cursor: dimmed ? 'pointer' : 'grab', overflow: 'hidden', boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
          opacity: dimmed ? 0.45 : 1, filter: dimmed ? 'grayscale(0.7)' : undefined,
        }}
      >
        {number !== undefined && <span style={{ fontSize: '0.72em', opacity: 0.85, fontWeight: 700, flexShrink: 0 }}>#{number}</span>}
        <span style={{ flex: 1, minWidth: 0 }}><FitText text={e.lastName} /></span>
        {dimmed && <span aria-hidden title="Sent home" style={{ fontSize: '0.72em', flexShrink: 0 }}>🏠</span>}
        {!dimmed && backAt && <span aria-hidden title="Back for a later shift" style={{ fontSize: '0.72em', flexShrink: 0 }}>↩</span>}
      </div>
    );
    // Prefer the full board-style card BoardView builds (identical to the room nameplates). If no builder was
    // supplied — or it could not resolve this id to an employee — fall back to the lightweight props built
    // from the brick's own record, so clicking a brick always opens a card.
    const rich = cardFor?.(e.employeeId);
    const card: BullpenCardProps = rich?.employee ? rich : {
      shift: providerCardShift(shift, orderOf(e.employeeId) ?? undefined),
      hospitalName: e.homeHospitalId ? hospitalNameById.get(e.homeHospitalId) : undefined,
      supervision: e.employeeType === 'doctor' && supervisionFor ? supervisionFor(e.employeeId) : undefined,
    };
    return (
      <EmployeeDetailsPopover
        key={e.employeeId}
        {...card}
        employee={rich?.employee ?? (e as unknown as Employee)}
        triggerStyle={{ display: 'block', width: '100%', textDecoration: 'none' }}
      >
        {inner}
      </EmployeeDetailsPopover>
    );
  };

  // `labelColor` tints the subgroup caption (used by the pecking-order groups, so the legend's
  // Pecking order colours are visible here too). The BRICKS keep their shift-derived colours, which
  // still encode each person's out-time -- that signal is not traded away for the group tint.
  const subgroup = (label: string, items: EmployeeLike[], colorFn: (e: EmployeeLike) => string, numbered = false, labelColor?: string) =>
    items.length === 0 ? null : (
      <div key={label} style={{ marginBottom: '8px' }}>
        <div style={{ fontSize: '0.62em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.3px', color: labelColor || 'var(--text-faint)', marginBottom: '3px' }}>{label}</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {items.map(e => brick(e, colorFn(e), numbered ? orderOf(e.employeeId)! : undefined))}
        </div>
      </div>
    );

  // Height of the shared NEED row: the nameplate's 22px button plus its 8px bottom margin. Reserved in
  // every column (see column() below) so a need in one column cannot shift the others out of line.
  const NEED_ROW_PX = 30;
  const anyNeed = doctorNeeds.length > 0 || crnaNeeds.length > 0;

  // Each column scrolls independently (its own vertical scrollbar) so a long list doesn't move the others.
  //
  // The heading is CENTRED over its own column and CLIPPED to it. Both matter: "ANESTHESIOLOGISTS" is a
  // single unbreakable word, so in a `flex: 1 1 0; minWidth: 0` column narrower than that word the text
  // had no break opportunity and overflowed to the right, into the CRNAs heading beside it. nowrap +
  // overflow:hidden keeps it inside its column (ellipsising in the worst case) instead of bleeding, and
  // staying on ONE line keeps all three columns' contents vertically aligned -- letting the long word
  // wrap would push this column's bricks down relative to its neighbours.
  //
  // The NEED box sits in its OWN row between the heading and the scroll box, and every column
  // reserves that row whenever ANY column has a need (NEED_ROW_PX). Two reasons:
  //   - Alignment. The boxes used to be the first child INSIDE each column's scroll area, so with a
  //     CRNA need but no MD need the two columns started at different heights and nothing lined up.
  //     Reserving the row in all three columns puts NEED MD and NEED CRNA on one line and keeps the
  //     Students bricks level with the other columns' bricks instead of riding up into the gap.
  //   - It no longer scrolls away: outside overflowY:auto, the need box stays pinned at the top of
  //     its column however far the list below is scrolled.
  const column = (title: string, count: number, children: React.ReactNode, need?: React.ReactNode) => (
    <div style={{ flex: '1 1 0', minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ fontSize: '0.7em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--text-muted)', marginBottom: '6px', textAlign: 'center', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', padding: '0 2px' }} title={`${title} (${count})`}>{title} ({count})</div>
      {anyNeed && <div style={{ minHeight: `${NEED_ROW_PX}px` }}>{need}</div>}
      <div style={{ maxHeight: '72vh', overflowY: 'auto', overflowX: 'hidden', paddingRight: '4px' }}>{children}</div>
    </div>
  );

  // A red "NEED MD" / "NEED CRNA" nameplate at the top of a column whenever rooms are still short that
  // provider. Click it to expand the list of rooms + the case times where that provider is needed.
  // `count` is the number of PEOPLE needed, which for doctors is not the number of rooms: one anesthesiologist
  // supervises several CRNA rooms, so 9 CRNA rooms short a supervisor is 3 MDs, not 9 (Braden 2026-09-19). The
  // dropdown still lists every room; only the badge counts people.
  const needNameplate = (kind: 'doctor' | 'crna', needs: StaffNeed[], count?: number) => {
    if (!needs.length) return null;
    const shown = count ?? needs.length;
    const label = kind === 'doctor' ? 'NEED MD' : 'NEED CRNA';
    const open = openNeed === kind;
    return (
      <div style={{ position: 'relative', marginBottom: '8px' }} onMouseDown={(e) => e.stopPropagation()}>
        <button
          onClick={(ev) => {
            const r = (ev.currentTarget as HTMLElement).getBoundingClientRect();
            setNeedAnchor({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
            setOpenNeed(open ? null : kind);
          }}
          title={shown === needs.length
            ? `${needs.length} room${needs.length === 1 ? '' : 's'} still need ${kind === 'doctor' ? 'an anesthesiologist' : 'a CRNA'} — click for the list`
            : `${shown} anesthesiologist${shown === 1 ? '' : 's'} needed to cover ${needs.length} rooms — click for the list`}
          style={{ width: '100%', background: 'rgba(220,38,38,0.14)', border: '1px solid #dc2626', color: '#dc2626', fontWeight: 700, borderRadius: '5px', padding: '0 6px', height: '22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: '0.78em', letterSpacing: '0.3px' }}
        >
          <span>{label}</span>
          <span style={{ fontSize: '0.85em', background: '#dc2626', color: '#fff', borderRadius: '999px', minWidth: '16px', textAlign: 'center', padding: '0 5px' }}>{shown}</span>
        </button>
        {open && (
          <>
            {/* Opaque scrim. The panel is position:fixed over the board, so without this the rooms
                behind it bleed through and the list is hard to read. Clicking it closes the panel. */}
            <div onClick={() => setOpenNeed(null)} style={{ position: 'fixed', inset: 0, zIndex: 1199, background: 'rgba(0,0,0,0.55)' }} />
            {/* Grouped BY SITE so the panel answers "where are the needs", and laid out as an auto-filling
                grid at a width the bullpen column could never give it. No inner scroll box: every need is
                on screen at once. The board used to show around and through this panel not because of
                its background (--popover-bg is opaque) but because nothing dimmed the board behind it and
                the shadow was soft -- so the fix is the scrim above plus a heavier border and shadow. */}
            <div style={{ position: 'fixed', top: `${needAnchor?.top ?? 120}px`, right: `${needAnchor?.right ?? 16}px`, zIndex: 1200, width: 'min(760px, 92vw)', background: 'var(--popover-bg)', border: '2px solid #dc2626', borderRadius: '8px', boxShadow: '0 18px 50px rgba(0,0,0,0.6)', padding: '10px 12px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: '8px' }}>
                <div style={{ fontSize: '0.74em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#dc2626' }}>
                  {label} — {shown === needs.length ? `${needs.length} room${needs.length === 1 ? '' : 's'}` : `${shown} needed · ${needs.length} rooms`}
                </div>
                <button onClick={() => setOpenNeed(null)} className="btn btn-secondary" style={{ padding: '0 8px', fontSize: '0.75em', height: '20px' }}>Close</button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))', gap: '10px', alignItems: 'start' }}>
                {needsBySite(needs).map(([hospital, items]) => {
                  // Each site card is built like the board's own hospital sections: the site's colour as a
                  // 2px frame with a darkened header bar, so a need at Uptown reads in Uptown's green. The
                  // panel keeps its red trim; only the cards inside are site-coloured.
                  const siteColor = colorForHospital(hospObjById.get(items[0]?.hospitalId || ''));
                  return (
                  <div key={hospital} style={{ border: `2px solid ${siteColor}`, borderRadius: '6px', overflow: 'hidden', minWidth: 0, background: 'var(--bg-surface)' }}>
                    <div style={{ background: darkenHex(siteColor, 0.4), color: '#fff', padding: '4px 8px', fontSize: '0.68em', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.3px', textShadow: '0 1px 2px rgba(0,0,0,0.4)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {hospital} <span style={{ opacity: 0.85 }}>({items.length})</span>
                    </div>
                    <div style={{ padding: '4px 8px 6px' }}>
                      {items.map((n, i) => (
                        <div key={i} style={{ fontSize: '0.8em', color: 'var(--text-primary)', padding: '2px 0', display: 'flex', gap: '8px', justifyContent: 'space-between' }}>
                          <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.roomLabel}</span>
                          {!!n.time && <span style={{ color: siteColor, fontWeight: 800, flexShrink: 0 }}>{n.time}</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </>
        )}
      </div>
    );
  };

  // A manual nameplate recolor (esp. an anesthesiologist recolored across their rooms) wins over the
  // shift-derived color so the bullpen brick matches the board.
  const docColor = (e: EmployeeLike) => colorOverrideFor?.(e.employeeId) || doctorBullpenColor(displayShift(e.employeeId));
  const crnaColor = (e: EmployeeLike) => colorOverrideFor?.(e.employeeId) || crnaNameplateColor(displayShift(e.employeeId));

  // Dropping a staffed plate here removes them from that room (and back into the bullpen if unstaffed).
  const removalDrop = onRemoveStaff ? {
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; },
    onDragEnter: (e: React.DragEvent) => { e.preventDefault(); setDropActive(true); },
    onDragLeave: (e: React.DragEvent) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDropActive(false); },
    onDrop: (e: React.DragEvent) => {
      setDropActive(false);
      const raw = e.dataTransfer.getData('application/x-staff');
      if (!raw) return;
      try {
        const { employeeId, fromScheduleId } = JSON.parse(raw);
        if (fromScheduleId && employeeId) onRemoveStaff(fromScheduleId, employeeId);
      } catch { /* ignore */ }
    },
  } : {};

  return (
    <div {...removalDrop} style={{ outline: dropActive ? '2px dashed var(--or-blue-mid)' : 'none', outlineOffset: '4px', borderRadius: '6px' }}>
      <div style={{ display: 'flex', gap: '12px' }}>
        {column('Anesthesiologists', doctors.length, <>
          {shiftDocGroups.map(g => subgroup(g.label, g.items.slice().sort(byLastName), docColor))}
          {subgroup('Pecking order', orderedDocs, docColor, true, NAMEPLATE_COLORS.peckingDoctor)}
        </>, needNameplate('doctor', doctorNeeds, doctorNeedCount))}
        {column('CRNAs', crnas.length, <>
          {shiftCrnaGroups.map(g => subgroup(g.label, g.items.slice().sort(byLastName), crnaColor))}
          {subgroup('Pecking order', orderedCrnas, crnaColor, true, NAMEPLATE_COLORS.peckingCrna)}
        </>, needNameplate('crna', crnaNeeds))}
        {column('Students', students.length, <>
          {/* Student tiers, most senior first. These are DISPLAY names only -- the stored
              studentCompetency values are unchanged ('green' | 'intermediate' | 'new'), so "Senior" is
              the green tier and "Junior" is the 'new' tier. Renaming the stored values would be a
              migration across the backend types, the employee forms and the allocator. The colours
              already line up: studentGreen green, studentIntermediate yellow, studentNew red. */}
          {subgroup('Senior', students.filter(e => e.employeeType === 'nurse-student-senior' || e.studentCompetency === 'green').sort(byLastName), e => studentColorFor(e.employeeType, e.studentCompetency))}
          {subgroup('Intermediate', students.filter(e => e.employeeType !== 'nurse-student-senior' && e.studentCompetency === 'intermediate').sort(byLastName), e => studentColorFor(e.employeeType, e.studentCompetency))}
          {subgroup('Junior', students.filter(e => e.employeeType !== 'nurse-student-senior' && e.studentCompetency === 'new').sort(byLastName), e => studentColorFor(e.employeeType, e.studentCompetency))}
          {subgroup('Competency not set', students.filter(e => e.employeeType !== 'nurse-student-senior' && !e.studentCompetency).sort(byLastName), e => studentColorFor(e.employeeType, e.studentCompetency))}
        </>)}
      </div>
    </div>
  );
};

export default Bullpen;
